import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import ConfirmationModal from "./ConfirmationModal";
import {
  getVarieties,
  createVariety,
  deleteVariety,
  updateVariety,
} from "../api/variety";
import { getSpecies } from "../api/species";
import "./TablesStyle.css";

const VarietiesTab = () => {
  const [varieties, setVarieties] = useState([]);
  const [species, setSpecies] = useState([]);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [visible, setVisible] = useState(false);
  const [varietiesForm, setVarietiesForm] = useState({
    name: "",
    specie_id: null,
  });
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [searchText, setSearchText] = useState("");
  const [nameError, setNameError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  useEffect(() => {
    loadVarieties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVarieties = async () => {
    try {
      const response = await getVarieties();
      setVarieties(response.data);
      const speciesResponse = await getSpecies();
      setSpecies(speciesResponse.data);
    } catch (error) {
      showToast("error", "Error", "Error al obtener las variedades");
      console.error("Error al obtener las variedades:", error);
    }
  };

  const openDialog = (variety) => {
    setSelectedVariety(variety);
    setVarietiesForm({
      name: variety?.name || "",
      specie_id: variety?.specie_id || null,
    });
    setVisible(true);
    setIsEditing(!!variety);
  };

  const hideDialog = () => {
    setVisible(false);
    setSelectedVariety(null);
    setVarietiesForm({
      name: "",
      specie_id: null,
    });
    setNameError("");
    setIsEditing(false);
  };

  const saveVariety = async () => {
    try {
      if (!varietiesForm.name) {
        setNameError("El nombre de la variedad es obligatorio");
        return;
      }

      await updateOrcreateVariety();
    } catch (error) {
      console.error("Error al guardar la variedad:", error);
      showToast("error", "Error", "Error al guardar la variedad");
    }
  };

  const updateOrcreateVariety = async () => {
    setDisableSave(true);
    try {
      if (selectedVariety) {
        await updateVariety(
          selectedVariety.id,
          varietiesForm.name,
          varietiesForm.specie_id
        );
      } else {
        await createVariety(varietiesForm.name, varietiesForm.specie_id);
      }
      loadVarieties();
      hideDialog();
      showToast("success", "Operación Exitosa", "Variedad guardada con éxito");
      setDisableSave(false);
    } catch (error) {
      console.error("Error al guardar la variedad:", error);
      showToast("error", "Error", "Error al guardar la variedad");
      setDisableSave(false);
    }
  };

  const showConfirmationModal = () => {
    setConfirmationModalVisible(true);
  };

  const hideConfirmationModal = () => {
    setConfirmationModalVisible(false);
  };

  const confirmDelete = async () => {
    try {
      await deleteVariety(selectedVariety.id);
      loadVarieties();
      hideDialog();
      showToast("success", "Operación Exitosa", "Variedad eliminada con éxito");
    } catch (error) {
      console.error("Error al eliminar la variedad:", error);
      showToast("error", "Error", "Error al eliminar la variedad");
    }
  };

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail });
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  const filteredVarieties = varieties.filter(
    (variety) =>
      variety.name.toLowerCase().includes(searchText.toLowerCase()) ||
      variety.species_name.toLowerCase().includes(searchText.toLowerCase())
  );

  const toast = React.useRef(null);

  return (
    <div className="Table">
      <div className="TableData">
        <h2>Variedades</h2>
        <div className="TableHeader">
          <div>
            <Button
              label="Agregar Variedad"
              icon="pi pi-plus"
              onClick={() => openDialog(null)}
              className="p-mb-2"
            />
          </div>
          <div>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                placeholder="Buscar"
                value={searchText}
                onChange={handleSearch}
              />
            </span>
          </div>
        </div>
        <DataTable
          value={filteredVarieties}
          selectionMode="single"
          onSelectionChange={(e) => openDialog(e.value)}
        >
          <Column field="name" header="Nombre" sortable></Column>
          <Column field="species_name" header="Especie" sortable></Column>
        </DataTable>
      </div>

      <Dialog
        visible={visible}
        onHide={hideDialog}
        header={isEditing ? "Editar Variedad" : "Nueva Variedad"}
      >
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="name">Nombre</label>
            <InputText
              id="name"
              value={varietiesForm.name}
              onChange={(e) => {
                setVarietiesForm({ ...varietiesForm, name: e.target.value });
                setNameError("");
              }}
            />
            {nameError && <small className="p-error">{nameError}</small>}
          </div>
          <div className="p-field">
            <label htmlFor="specie_id">Especie</label>
            <Dropdown
              id="specie_id"
              optionLabel="name"
              optionValue="id"
              value={varietiesForm.specie_id}
              options={species}
              onChange={(e) =>
                setVarietiesForm({
                  ...varietiesForm,
                  specie_id: e.value,
                })
              }
              placeholder="Seleccione una especie"
            />
          </div>
        </div>
        <div className="p-mt-2">
          <Button
            label="Guardar"
            icon="pi pi-check"
            onClick={saveVariety}
            disabled={disableSave}
          />
          <Button
            label="Cancelar"
            icon="pi pi-times"
            className="p-button-secondary p-ml-2"
            onClick={hideDialog}
          />
          {selectedVariety && (
            <Button
              label="Eliminar"
              icon="pi pi-trash"
              className="p-button-danger p-ml-2"
              onClick={showConfirmationModal}
            />
          )}
        </div>
      </Dialog>

      <ConfirmationModal
        visible={confirmationModalVisible}
        onHide={hideConfirmationModal}
        onConfirm={confirmDelete}
        onCancel={hideConfirmationModal}
        message={"esta variedad"}
      />

      <Toast ref={toast} />
    </div>
  );
};

export default VarietiesTab;
